/**
 * HitlOverviewReport — Modernization Readiness Report
 *
 * Story-driven tabbed report. Answers:
 *   1. What did we find?
 *   2. How bad is it?
 *   3. Where is the risk?
 *   4. What should I review first?
 *   5. What is missing for this run?
 *
 * Tabs:
 *   Overview    — readiness story, risk focus, recommendation breakdown
 *   Code Health — risk ranking, program deep-dive, service split candidates
 *   Actions     — action summary strip, issue breakdown, review list
 *
 * Props:
 *   rcJobId   — refactor_code job ID (S3 data)
 *   apiBase   — API base URL
 *   accountId — account ID (MongoDB)
 *   appName   — application name
 */

import { useState, useEffect, useMemo, useRef } from 'react'
import * as echarts from 'echarts'
import {
  Loader2, AlertCircle, Layers, GitBranch, Box,
  ChevronDown, ChevronRight,
  CheckCircle2, Clock, MinusCircle,
  FileCode, Shield, Activity, ListChecks, LayoutDashboard,
} from 'lucide-react'
import './HitlOverviewReport.css'

function authHeaders() {
  const token = localStorage.getItem('modernizeit_token')
  return token ? { Authorization: `Bearer ${token}` } : {}
}

// ── Issue labels — plain English, no jargon ───────────────────────────────────

const ISSUE_LABELS = {
  LCOM4_SPLIT:         'Service Split Candidate',
  LONG_METHOD:         'Method Too Long',
  LONG_PARAGRAPH:      'Paragraph Too Long',
  MVC_MIXED:           'Mixed Concerns',
  COBOL_NAMING:        'Naming Convention',
  COBOL_IDENTIFIER:    'Naming Convention',
  DEAD_CODE:           'Unused Code',
  DEAD_PARAGRAPH:      'Unused Block',
  DEAD_METHOD:         'Unused Method',
  GOD_CLASS:           'Class Too Large',
  LARGE_CLASS:         'Class Too Large',
  FEATURE_ENVY:        'Misplaced Logic',
  HIGH_CC:             'High Complexity',
  DEEP_NESTING:        'Deep Nesting',
  GOTO_USAGE:          'GOTO Usage',
  EVALUATE_STRATEGY:   'Complex Branching',
  CIRCULAR_DEPENDENCY: 'Circular Dependency',
  EMPTY_CATCH:         'Empty Error Handler',
  COMMENTED_CODE:      'Commented-Out Code',
  PRIMITIVE_OBSESSION: 'Primitive Overuse',
  DUPLICATE_CODE:      'Duplicate Code',
}
const issueLabel = (t) => ISSUE_LABELS[t] || t

function issueColor(type) {
  if (!type) return 'gray'
  if (type.includes('LCOM4') || type === 'GOD_CLASS' || type === 'LARGE_CLASS') return 'purple'
  if (type.includes('MVC'))  return 'orange'
  if (type.includes('NAMING') || type.includes('IDENTIFIER')) return 'cyan'
  if (type === 'HIGH_CC' || type === 'DEEP_NESTING' || type === 'CIRCULAR_DEPENDENCY') return 'red'
  if (type.includes('DEAD') || type === 'EMPTY_CATCH' || type === 'COMMENTED_CODE') return 'gray'
  return 'blue'
}

const ISSUE_HEX = { purple:'#a78bfa', orange:'#ff9e64', cyan:'#7dcfff', red:'#f87171', gray:'#565f89', blue:'#7aa2f7' }

// ── Helpers ───────────────────────────────────────────────────────────────────

function statusLabel(d) {
  return d === 'confirmed' ? 'confirmed' : d === 'skipped' ? 'skipped' : 'pending'
}

/** Build a plain-English one-liner from real data only */
function buildStorySummary(prog, ingest, counts) {
  const parts = []
  if (ingest?.cobol_programs > 0)
    parts.push(`${ingest.cobol_programs} COBOL program${ingest.cobol_programs !== 1 ? 's' : ''} analyzed`)
  if (counts?.all > 0)
    parts.push(`${counts.all} recommendation${counts.all !== 1 ? 's' : ''} found`)
  if (counts?.pending > 0)
    parts.push(`${counts.pending} open`)
  if (prog?.program_name)
    parts.push(`highest risk is ${prog.program_name}`)
  return parts.join(' · ')
}

/** Generate "why it matters" from real program fields */
function buildWhyItMatters(p) {
  if (!p) return null
  const f = []
  if (p.cyclomatic_complexity > 10) f.push(`high complexity (${p.cyclomatic_complexity})`)
  else if (p.cyclomatic_complexity > 5) f.push(`complexity score ${p.cyclomatic_complexity}`)
  if (p.fan_out > 8) f.push(`${p.fan_out} outgoing dependencies`)
  else if (p.fan_out > 4) f.push(`${p.fan_out} outgoing calls`)
  if (p.instability > 0.7) f.push('high coupling')
  if (p.lcom4_clusters > 1) f.push(`${p.lcom4_clusters} service split groups`)
  if (f.length === 0 && p.lines_of_code > 500) f.push(`${p.lines_of_code.toLocaleString()} lines of code`)
  if (f.length === 0) return null
  return `${f.join(', ')} — increases modernization complexity.`
}

// ── Status icon ───────────────────────────────────────────────────────────────

function StatusIcon({ decision }) {
  if (decision === 'confirmed') return <CheckCircle2 size={13} className="hor-status-icon confirmed" />
  if (decision === 'skipped')   return <MinusCircle  size={13} className="hor-status-icon skipped" />
  return <Clock size={13} className="hor-status-icon pending" />
}

// ── Story Header ──────────────────────────────────────────────────────────────
// The "readiness verdict" card — sits above everything.

function StoryHeader({ prog, ingest, counts, worstRisk, worstRiskTier, gateStatus, appName }) {
  const summary = buildStorySummary(prog, ingest, counts)
  return (
    <div className="hor-story-header">
      <div className="hor-story-body">
        <div className="hor-story-title">Modernization Readiness</div>
        {appName && <div className="hor-story-app">{appName}</div>}
        {summary && <div className="hor-story-summary">{summary}</div>}
      </div>
      <div className="hor-story-verdict">
        {worstRisk ? (
          <div className={`hor-story-risk-badge hor-risk-badge--${worstRiskTier}`}>
            {worstRisk}
          </div>
        ) : (
          <div className="hor-story-risk-badge hor-risk-badge--unknown">—</div>
        )}
        <div className="hor-story-risk-label">Overall Risk</div>
        {gateStatus && (
          <div className={`hor-story-gate hor-story-gate--${gateStatus === 'PASS' ? 'pass' : 'fail'}`}>
            {gateStatus === 'PASS' ? '✓ Build Ready' : '✗ Build Issues'}
          </div>
        )}
      </div>
    </div>
  )
}

// ── Metric strip ──────────────────────────────────────────────────────────────

function MetricStrip({ items }) {
  if (!items.length) return null
  return (
    <div className="hor-metric-strip">
      {items.map((m, i) => (
        <div key={i} className={`hor-metric-card${m.color ? ` hor-metric-card--${m.color}` : ''}`}>
          <div className="hor-metric-value">{m.value}</div>
          <div className="hor-metric-label">{m.label}</div>
        </div>
      ))}
    </div>
  )
}

// ── Risk Focus card ───────────────────────────────────────────────────────────
// Left column — deep-dive on the highest-risk program

function RiskFocusCard({ prog, clusters }) {
  if (!prog) return (
    <div className="hor-panel hor-panel--flex">
      <div className="hor-panel-hdr"><span className="hor-panel-title">Highest Risk Program</span></div>
      <div className="hor-unavailable">Program risk data not available for this run.</div>
    </div>
  )

  const why = buildWhyItMatters(prog)

  return (
    <div className="hor-panel hor-panel--flex">
      <div className="hor-panel-hdr">
        <span className="hor-panel-title">Highest Risk Program</span>
      </div>
      <div className="hor-risk-focus-name">{prog.program_name}</div>
      <span className={`hor-risk-pill hor-risk-pill--${prog.risk_tier || 'unknown'}`}>
        {prog.risk_tier ? prog.risk_tier.toUpperCase() : '—'}
      </span>
      {why && <div className="hor-risk-focus-why">{why}</div>}
      <div className="hor-stat-rows">
        {prog.lines_of_code   != null && <StatRow label="Lines of Code"    value={prog.lines_of_code.toLocaleString()} />}
        {prog.cyclomatic_complexity != null && <StatRow label="Complexity"  value={prog.cyclomatic_complexity} />}
        {prog.fan_out         != null && <StatRow label="Outgoing Calls"    value={prog.fan_out} />}
        {prog.instability     != null && <StatRow label="Coupling"          value={prog.instability.toFixed(2)} />}
        {prog.lcom4_clusters  != null && clusters > 1 && (
          <StatRow label="Service Splits" value={prog.lcom4_clusters} highlight />
        )}
        {prog.dependencies?.length > 0 && <StatRow label="Dependencies"  value={prog.dependencies.length} />}
      </div>
    </div>
  )
}

function StatRow({ label, value, highlight }) {
  return (
    <div className="hor-stat-row">
      <span className="hor-stat-row-label">{label}</span>
      <span className={`hor-stat-row-value${highlight ? ' hor-stat-highlight' : ''}`}>{value}</span>
    </div>
  )
}

// ── Transformation / HITL status panel ───────────────────────────────────────

function TransformationPanel({ javaGen, avgConf, pfPassed, pfFiles, pfErrors, counts }) {
  const hasDecisions = counts.all > 0

  return (
    <div className="hor-panel hor-panel--flex">
      <div className="hor-panel-hdr">
        <span className="hor-panel-title">Transformation & HITL</span>
      </div>
      <div className="hor-stat-rows">

        {/* Quality Gate — a row, not a hero */}
        <div className="hor-stat-row">
          <span className="hor-stat-row-label">Quality Gate</span>
          {pfPassed === true  && <span className="hor-gate-pass">✓ PASS{pfFiles ? ` · ${pfFiles} files` : ''}</span>}
          {pfPassed === false && <span className="hor-gate-fail">✗ FAIL · {pfErrors ?? 0} errors</span>}
          {pfPassed == null   && <span className="hor-stat-na">Not available</span>}
        </div>

        {javaGen != null && (
          <StatRow label="Java Files Generated" value={javaGen} />
        )}
        {avgConf && (
          <div className="hor-stat-row">
            <span className="hor-stat-row-label">AI Naming Confidence</span>
            <span className="hor-stat-row-value" style={{ color: '#e0af68' }}>{avgConf}</span>
          </div>
        )}

        {hasDecisions ? (
          <>
            <div className="hor-stat-row">
              <span className="hor-stat-row-label">Recommendations</span>
              <span className="hor-stat-row-value">{counts.all} total</span>
            </div>
            <div className="hor-hitl-decision-row">
              <span className="hor-decision-chip hor-decision-confirmed">{counts.confirmed} confirmed</span>
              <span className="hor-decision-chip hor-decision-skipped">{counts.skipped} skipped</span>
              <span className="hor-decision-chip hor-decision-pending">{counts.pending} open</span>
            </div>
          </>
        ) : (
          <div className="hor-stat-row">
            <span className="hor-stat-row-label">HITL Decisions</span>
            <span className="hor-stat-na">Not available for this run</span>
          </div>
        )}

      </div>
    </div>
  )
}

// ── Compact issue bars — CSS only, no ECharts overhead for small datasets ─────

function CompactIssueBars({ allRecs }) {
  const counts = useMemo(() => {
    const c = {}
    allRecs.forEach(r => {
      const lbl = issueLabel(r.smell_type)
      c[lbl] = (c[lbl] || 0) + 1
    })
    return c
  }, [allRecs])

  const sorted = Object.entries(counts).sort((a, b) => b[1] - a[1]).slice(0, 7)
  if (!sorted.length) return null
  const max = sorted[0][1]

  return (
    <div className="hor-compact-bars">
      {sorted.map(([label, count]) => (
        <div key={label} className="hor-compact-bar-row">
          <span className="hor-compact-bar-label">{label}</span>
          <div className="hor-compact-bar-track">
            <div
              className="hor-compact-bar-fill"
              style={{ width: `${Math.round((count / max) * 100)}%` }}
            />
          </div>
          <span className="hor-compact-bar-count">{count}</span>
        </div>
      ))}
    </div>
  )
}

// ── Action summary strip ──────────────────────────────────────────────────────

function ActionSummaryStrip({ counts, allRecs }) {
  const highConf = allRecs.filter(r => (r.confidence || 0) >= 0.8).length
  const items = [
    { label: 'Total',          value: counts.all,       color: '' },
    { label: 'Open',           value: counts.pending,   color: 'pending' },
    { label: 'Confirmed',      value: counts.confirmed, color: 'confirmed' },
    { label: 'Skipped',        value: counts.skipped,   color: 'skipped' },
    ...(highConf > 0 ? [{ label: '≥ 80% Confidence', value: highConf, color: 'highconf' }] : []),
  ]
  return (
    <div className="hor-action-strip">
      {items.map((it, i) => (
        <div key={i} className={`hor-action-stat${it.color ? ` hor-action-${it.color}` : ''}`}>
          <div className="hor-action-stat-value">{it.value}</div>
          <div className="hor-action-stat-label">{it.label}</div>
        </div>
      ))}
    </div>
  )
}

// ── Recommendation row ────────────────────────────────────────────────────────

function RecRow({ rec }) {
  const [expanded, setExpanded] = useState(false)
  const status  = statusLabel(rec.user_decision)
  const color   = issueColor(rec.smell_type)
  const confPct = rec.confidence != null ? Math.round(rec.confidence * 100) : null

  return (
    <div className={`hor-rec-card ${status}`}>
      <button className="hor-rec-hdr" onClick={() => setExpanded(v => !v)}>
        <StatusIcon decision={rec.user_decision} />
        <span className="hor-rec-title">{rec.title || issueLabel(rec.smell_type)}</span>
        <span className={`hor-issue-badge ${color}`}>{issueLabel(rec.smell_type)}</span>
        {rec.cobol_origin?.program && (
          <span className="hor-cobol-tag">
            <FileCode size={11} />
            {rec.cobol_origin.program}
            {rec.cobol_origin.paragraph ? ` · ${rec.cobol_origin.paragraph}` : ''}
          </span>
        )}
        {confPct != null && <span className="hor-conf-tag">{confPct}%</span>}
        {expanded ? <ChevronDown size={12} className="hor-chev" /> : <ChevronRight size={12} className="hor-chev" />}
      </button>

      {expanded && (
        <div className="hor-rec-body">
          {rec.why && <p className="hor-rec-why">{rec.why}</p>}
          {rec.before?.snippet && (
            <div className="hor-snippet-block">
              <span className="hor-snippet-lbl">Before</span>
              <pre className="hor-snippet">{rec.before.snippet}</pre>
            </div>
          )}
          {rec.after?.description && (
            <div className="hor-snippet-block">
              <span className="hor-snippet-lbl">Suggested change</span>
              <p className="hor-after-desc">{rec.after.description}</p>
            </div>
          )}
          {rec.after?.snippet && (
            <div className="hor-snippet-block">
              <span className="hor-snippet-lbl">After</span>
              <pre className="hor-snippet">{rec.after.snippet}</pre>
            </div>
          )}
          {rec.risk?.description && (
            <div className="hor-risk-note">
              <Shield size={11} />
              <span className={`hor-risk-badge ${rec.risk.type?.toLowerCase() || ''}`}>{rec.risk.type}</span>
              <span>{rec.risk.description}</span>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

// ── Service split card ────────────────────────────────────────────────────────

function SplitCandidateCard({ cluster, index }) {
  const [expanded, setExpanded] = useState(false)
  const methodCount = cluster.methods?.length || 0
  const fieldCount  = (cluster.owned_fields?.length || 0) + (cluster.shared_fields?.length || 0)

  return (
    <div className="hor-split-card">
      <button className="hor-split-hdr" onClick={() => setExpanded(v => !v)}>
        <Box size={12} className="hor-split-ico" />
        <span className="hor-split-name">{cluster.class_name || `Group ${index + 1}`}</span>
        <span className="hor-split-badge">{methodCount} methods</span>
        {fieldCount > 0 && <span className="hor-split-badge field">{fieldCount} fields</span>}
        {expanded ? <ChevronDown size={11} className="hor-chev" /> : <ChevronRight size={11} className="hor-chev" />}
      </button>
      {expanded && (
        <div className="hor-split-body">
          {cluster.methods?.length > 0 && (
            <div className="hor-chips-group">
              <span className="hor-chips-label">Methods</span>
              {cluster.methods.map((m, i) => <span key={i} className="hor-chip method">{m}</span>)}
            </div>
          )}
          {cluster.owned_fields?.length > 0 && (
            <div className="hor-chips-group">
              <span className="hor-chips-label">Owned fields</span>
              {cluster.owned_fields.map((f, i) => <span key={i} className="hor-chip owned">{f}</span>)}
            </div>
          )}
          {cluster.shared_fields?.length > 0 && (
            <div className="hor-chips-group">
              <span className="hor-chips-label">Shared fields</span>
              {cluster.shared_fields.map((f, i) => <span key={i} className="hor-chip shared">{f}</span>)}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

// ── Code Risk Ranking chart ───────────────────────────────────────────────────

function RiskRankingChart({ programs }) {
  const chartRef    = useRef(null)
  const instanceRef = useRef(null)

  const ranked = useMemo(() => {
    const score = p => p.risk_score != null ? p.risk_score * 100
      : (p.cyclomatic_complexity || 0) + (p.lines_of_code || 0) / 100
    return [...programs].sort((a, b) => score(b) - score(a)).slice(0, 12).reverse()
  }, [programs])

  useEffect(() => {
    if (!chartRef.current || !ranked.length) return
    if (instanceRef.current) instanceRef.current.dispose()

    const chart = echarts.init(chartRef.current)
    instanceRef.current = chart

    const RISK_COLOR = { high: '#f87171', medium: '#f59e0b', low: '#9ece6a' }
    const riskScore = p => p.risk_score != null
      ? Math.round(p.risk_score * 100)
      : Math.min(100, (p.cyclomatic_complexity || 0) * 4 + (p.fan_out || 0) * 2)

    chart.setOption({
      backgroundColor: 'transparent',
      grid: { left: 120, right: 90, top: 8, bottom: 8, containLabel: false },
      tooltip: {
        trigger: 'item',
        backgroundColor: '#16161e',
        borderColor: '#3b4261',
        textStyle: { color: '#c0caf5', fontSize: 12 },
        formatter: (params) => {
          const p = ranked[params.dataIndex]
          return [
            `<strong>${p.program_name}</strong>`,
            `Risk: <span style="color:${RISK_COLOR[p.risk_tier] || '#565f89'}">${(p.risk_tier || '—').toUpperCase()}</span>`,
            `LOC: ${(p.lines_of_code || 0).toLocaleString()}`,
            `Complexity: ${p.cyclomatic_complexity ?? '—'}`,
            `Outgoing: ${p.fan_out ?? '—'}`,
          ].join('<br/>')
        },
      },
      xAxis: { type: 'value', max: 100, show: false },
      yAxis: {
        type: 'category',
        data: ranked.map(p => p.program_name),
        axisLabel: { color: '#a9b1d6', fontSize: 11, width: 110, overflow: 'truncate' },
        axisTick: { show: false }, axisLine: { show: false },
      },
      series: [{
        type: 'bar', barMaxWidth: 14,
        data: ranked.map(p => ({
          value: riskScore(p),
          itemStyle: { color: RISK_COLOR[p.risk_tier] || '#565f89', borderRadius: [0, 3, 3, 0] },
        })),
        label: {
          show: true, position: 'right', color: '#565f89', fontSize: 10,
          formatter: (params) => `${(ranked[params.dataIndex].lines_of_code || 0).toLocaleString()} LOC`,
        },
      }],
    })

    const onResize = () => chart.resize()
    window.addEventListener('resize', onResize)
    return () => { window.removeEventListener('resize', onResize); chart.dispose() }
  }, [ranked])

  if (!ranked.length) return <div className="hor-unavailable">No program data available.</div>

  return <div ref={chartRef} style={{ width: '100%', height: `${Math.max(180, ranked.length * 28)}px` }} />
}

// ── Issue breakdown chart (ECharts — for Actions tab) ─────────────────────────

function IssueBreakdownChart({ allRecs }) {
  const chartRef    = useRef(null)
  const instanceRef = useRef(null)

  const counts = useMemo(() => {
    const c = {}
    allRecs.forEach(r => { const l = issueLabel(r.smell_type); c[l] = (c[l] || 0) + 1 })
    return c
  }, [allRecs])

  useEffect(() => {
    if (!chartRef.current || !allRecs.length) return
    if (instanceRef.current) instanceRef.current.dispose()

    const chart = echarts.init(chartRef.current)
    instanceRef.current = chart
    const sorted = Object.entries(counts).sort((a, b) => b[1] - a[1]).slice(0, 8)
    if (!sorted.length) return

    chart.setOption({
      backgroundColor: 'transparent',
      grid: { left: 160, right: 50, top: 8, bottom: 8, containLabel: false },
      xAxis: { type: 'value', splitLine: { lineStyle: { color: '#2a2b3d' } }, axisLabel: { color: '#565f89', fontSize: 10 }, axisLine: { show: false }, axisTick: { show: false } },
      yAxis: { type: 'category', data: sorted.map(([k]) => k).reverse(), axisLabel: { color: '#a9b1d6', fontSize: 11 }, axisTick: { show: false }, axisLine: { show: false } },
      series: [{ type: 'bar', data: sorted.map(([, v]) => v).reverse(), barMaxWidth: 14, itemStyle: { color: '#7aa2f7', borderRadius: [0, 3, 3, 0] }, label: { show: true, position: 'right', color: '#565f89', fontSize: 10 } }],
    })

    const onResize = () => chart.resize()
    window.addEventListener('resize', onResize)
    return () => { window.removeEventListener('resize', onResize); chart.dispose() }
  }, [counts, allRecs.length])

  if (!allRecs.length) return null
  const chartH = Math.max(140, Object.keys(counts).slice(0, 8).length * 30)
  return <div ref={chartRef} style={{ width: '100%', height: `${chartH}px` }} />
}

// ── Tab config ────────────────────────────────────────────────────────────────

const TABS = [
  { id: 'overview', label: 'Overview',    Icon: LayoutDashboard },
  { id: 'health',   label: 'Code Health', Icon: Activity        },
  { id: 'actions',  label: 'Actions',     Icon: ListChecks      },
]

// ── Filters for Actions tab ───────────────────────────────────────────────────

const FILTERS = ['all', 'pending', 'confirmed', 'skipped']

// ═════════════════════════════════════════════════════════════════════════════
// Main component
// ═════════════════════════════════════════════════════════════════════════════

export default function HitlOverviewReport({ rcJobId, apiBase, accountId, appName }) {
  const [activeTab,   setActiveTab]   = useState('overview')
  const [lcom4,       setLcom4]       = useState(null)
  const [analysis,    setAnalysis]    = useState(null)
  const [loading,     setLoading]     = useState(true)
  const [error,       setError]       = useState(null)
  const [filter,      setFilter]      = useState('all')
  const [mongoProgram,setMongoProgram]= useState(null)
  const [mongoRun,    setMongoRun]    = useState(null)
  const [mongoLoading,setMongoLoading]= useState(false)
  const [allPrograms, setAllPrograms] = useState([])

  // ── S3 fetch (refactor analysis + LCOM4 graph) ────────────────────────────
  useEffect(() => {
    if (!rcJobId || !apiBase) return
    setLoading(true); setError(null)
    const h = authHeaders()
    const base = `${apiBase}/refactor-code/results/json/${rcJobId}`
    Promise.all([
      fetch(`${base}/lcom4_graph.json`,       { headers: h }).then(r => r.ok ? r.json() : null),
      fetch(`${base}/refactor_analysis.json`, { headers: h }).then(r => r.ok ? r.json() : null),
    ])
      .then(([l, a]) => { setLcom4(l); setAnalysis(a) })
      .catch(e => setError(e.message))
      .finally(() => setLoading(false))
  }, [rcJobId, apiBase])

  // ── MongoDB fetch (run doc + programs) ───────────────────────────────────
  useEffect(() => {
    if (!apiBase || !accountId || !appName) return
    setMongoLoading(true)
    const h = authHeaders()
    const get = (url) => fetch(url, { headers: h }).then(r => r.ok ? r.json() : null).catch(() => null)
    get(`${apiBase}/reporting/runs?account_id=${encodeURIComponent(accountId)}&app_name=${encodeURIComponent(appName)}&limit=5`)
      .then(async data => {
        const run = data?.runs?.[0]
        if (!run) return
        setMongoRun(run)
        if (run.run_id) {
          const pd = await get(`${apiBase}/reporting/runs/${run.run_id}/programs`)
          const progs = pd?.programs || []
          setAllPrograms(progs)
          if (progs.length) {
            setMongoProgram([...progs].sort((a, b) => (b.risk_score || 0) - (a.risk_score || 0))[0])
          }
        }
      })
      .finally(() => setMongoLoading(false))
  }, [apiBase, accountId, appName])

  // ── Derived ───────────────────────────────────────────────────────────────
  const allRecs = useMemo(() => {
    if (!analysis?.services) return []
    return analysis.services.flatMap(svc => (svc.files || []).flatMap(f => f.recommendations || []))
  }, [analysis])

  const filteredRecs = useMemo(() => {
    if (filter === 'all')     return allRecs
    if (filter === 'pending') return allRecs.filter(r => !r.user_decision)
    return allRecs.filter(r => r.user_decision === filter)
  }, [allRecs, filter])

  const topPriorityRecs = useMemo(() =>
    [...allRecs].filter(r => r.confidence != null).sort((a, b) => (b.confidence || 0) - (a.confidence || 0)).slice(0, 3),
    [allRecs]
  )

  if (loading) return (
    <div className="hor-loading"><Loader2 size={20} className="spin" /><span>Loading report…</span></div>
  )

  if (error || (!lcom4 && !analysis)) return (
    <div className="hor-error"><AlertCircle size={16} /><span>{error || 'No report data available for this run.'}</span></div>
  )

  // ── Computed values ───────────────────────────────────────────────────────
  const clusters     = lcom4?.clusters     || []
  const sharedFields = lcom4?.shared_fields || []
  const summary      = analysis?.summary   || {}
  const totalRecs    = summary.total_recommendations || allRecs.length
  const prog         = mongoProgram

  const counts = {
    all:       allRecs.length,
    pending:   allRecs.filter(r => !r.user_decision).length,
    confirmed: allRecs.filter(r => r.user_decision === 'confirmed').length,
    skipped:   allRecs.filter(r => r.user_decision === 'skipped').length,
  }

  const ingest       = mongoRun?.ingest_summary    || {}
  const svcgen       = mongoRun?.svcgen_summary    || {}
  const aiSum        = mongoRun?.ai_naming_summary || {}
  const preFlight    = mongoRun?.pre_flight_compile || {}
  const pfPassed     = preFlight.passed
  const pfErrors     = preFlight.error_count ?? 0
  const pfFiles      = preFlight.files_compiled ?? null
  const javaGen      = svcgen.services_generated ?? svcgen.java_files_generated ?? null
  const avgConf      = aiSum.avg_confidence != null ? `${Math.round(aiSum.avg_confidence * 100)}%` : null

  const highCount    = allPrograms.filter(p => p.risk_tier === 'high').length
  const medCount     = allPrograms.filter(p => p.risk_tier === 'medium').length
  const lowCount     = allPrograms.filter(p => p.risk_tier === 'low').length
  const worstRiskTier= highCount > 0 ? 'high' : medCount > 0 ? 'medium' : allPrograms.length > 0 ? 'low' : null
  const worstRisk    = worstRiskTier === 'high' ? 'HIGH' : worstRiskTier === 'medium' ? 'MED' : worstRiskTier === 'low' ? 'LOW' : null
  const gateStatus   = pfPassed === true ? 'PASS' : pfPassed === false ? 'FAIL' : null

  // Metric strip items — only show items that have real data
  const metricItems = [
    ingest.cobol_programs != null && { label: 'Programs',       value: ingest.cobol_programs },
    counts.all > 0               && { label: 'Recommendations', value: counts.all            },
    counts.all > 0               && { label: 'Open Actions',    value: counts.pending, color: counts.pending > 0 ? 'pending' : null },
    worstRisk                    && { label: 'Highest Risk',    value: worstRisk, color: worstRiskTier },
    counts.all > 0               && { label: 'HITL Decisions',  value: `${counts.confirmed} / ${counts.all}` },
    gateStatus                   && { label: 'Quality Gate',    value: gateStatus, color: gateStatus === 'PASS' ? 'pass' : 'fail' },
  ].filter(Boolean)

  // Data availability messages — only mention what's actually missing
  const missingData = []
  if (!mongoRun && !mongoLoading)             missingData.push('Asset and run data not available for this run.')
  if (!ingest.cobol_programs && mongoRun)     missingData.push('Asset composition is not available.')
  if (pfPassed == null)                       missingData.push('Build verification (quality gate) is not available for this run.')
  if (counts.all === 0)                       missingData.push('HITL decisions are not available for this run.')

  // Code Health tab — interpretation line
  const healthInterpretation = prog ? (() => {
    const name = prog.program_name
    const factors = []
    if (prog.cyclomatic_complexity > 5) factors.push(`complexity ${prog.cyclomatic_complexity}`)
    if (prog.fan_out > 4)               factors.push(`${prog.fan_out} outgoing calls`)
    if (prog.instability > 0.5)         factors.push('high coupling')
    if (!factors.length)                return null
    return `${name} is the highest-risk program based on ${factors.join(', ')}.`
  })() : null

  return (
    <div className="hor-root">

      {/* ── Header ── */}
      <div className="hor-header">
        <div className="hor-header-left">
          <Layers size={15} className="hor-header-ico" />
          <span className="hor-header-title">Code Health Report</span>
          <span className="hor-header-sub">{appName || analysis?.app_name || ''}</span>
        </div>
        <span className="hor-job-id" title={rcJobId}>…{rcJobId?.slice(-10)}</span>
      </div>

      {/* ── Tab bar ── */}
      <div className="hor-tab-bar">
        {TABS.map(({ id, label, Icon }) => {
          const badge = id === 'actions' && counts.all > 0 ? counts.all : null
          return (
            <button
              key={id}
              className={`hor-tab ${activeTab === id ? 'active' : ''}`}
              onClick={() => setActiveTab(id)}
            >
              <Icon size={13} />
              {label}
              {badge != null && <span className="hor-tab-badge">{badge}</span>}
            </button>
          )
        })}
      </div>

      {/* ══════════════════ OVERVIEW TAB ══════════════════════════════════════ */}
      {activeTab === 'overview' && (
        <div className="hor-tab-content">

          {/* 1. Readiness story header */}
          <StoryHeader
            prog={prog}
            ingest={ingest}
            counts={counts}
            worstRisk={worstRisk}
            worstRiskTier={worstRiskTier}
            gateStatus={gateStatus}
            appName={appName}
          />

          {/* 2. Metric strip */}
          {metricItems.length > 0 && <MetricStrip items={metricItems} />}

          {/* 3. Two-column story section */}
          <div className="hor-dash-row">
            <RiskFocusCard prog={prog} clusters={clusters.length} />
            <TransformationPanel
              javaGen={javaGen}
              avgConf={avgConf}
              pfPassed={pfPassed}
              pfFiles={pfFiles}
              pfErrors={pfErrors}
              counts={counts}
            />
          </div>

          {/* 4. Recommendation breakdown — compact bars, no big chart */}
          {allRecs.length > 0 && (
            <div className="hor-panel">
              <div className="hor-panel-hdr">
                <span className="hor-panel-title">What kinds of issues were found?</span>
                <span className="hor-panel-badge">{counts.all} total</span>
              </div>
              <CompactIssueBars allRecs={allRecs} />
            </div>
          )}

          {/* 5. Top priority actions */}
          {topPriorityRecs.length > 0 && (
            <div className="hor-panel">
              <div className="hor-panel-hdr">
                <span className="hor-panel-title">What should I review first?</span>
                <span className="hor-panel-subtitle">Sorted by confidence · see Actions tab for full list</span>
              </div>
              <div className="hor-priority-row">
                {topPriorityRecs.map((rec, i) => <PriorityCard key={rec.id || i} rec={rec} />)}
              </div>
            </div>
          )}

          {/* 6. Data availability — at the bottom, not up top */}
          {missingData.length > 0 && (
            <div className="hor-data-avail">
              <span className="hor-data-avail-title">Data Availability</span>
              {missingData.map((msg, i) => (
                <span key={i} className="hor-data-avail-item">{msg}</span>
              ))}
            </div>
          )}

        </div>
      )}

      {/* ══════════════════ CODE HEALTH TAB ═══════════════════════════════════ */}
      {activeTab === 'health' && (
        <div className="hor-tab-content">

          {/* Risk ranking — full width */}
          {allPrograms.length > 0 ? (
            <div className="hor-panel">
              <div className="hor-panel-hdr">
                <span className="hor-panel-title">Code Risk Ranking</span>
                <span className="hor-panel-subtitle">Highest risk at top · hover for details</span>
                <div className="hor-risk-chips">
                  {highCount > 0 && <span className="hor-risk-chip high">{highCount} High</span>}
                  {medCount  > 0 && <span className="hor-risk-chip medium">{medCount} Med</span>}
                  {lowCount  > 0 && <span className="hor-risk-chip low">{lowCount} Low</span>}
                </div>
              </div>
              {healthInterpretation && (
                <div className="hor-interpretation">{healthInterpretation}</div>
              )}
              <RiskRankingChart programs={allPrograms} />
            </div>
          ) : (
            <div className="hor-panel">
              <div className="hor-panel-hdr"><span className="hor-panel-title">Code Risk Ranking</span></div>
              <div className="hor-unavailable">Code risk details are not available for this run.</div>
            </div>
          )}

          {/* Highest-risk program deep-dive */}
          {prog && (
            <div className="hor-dash-row">
              <RiskFocusCard prog={prog} clusters={clusters.length} />
              <div className="hor-panel hor-panel--flex">
                <div className="hor-panel-hdr"><span className="hor-panel-title">All Programs</span></div>
                {allPrograms.length > 0 ? (
                  <div className="hor-prog-table">
                    {allPrograms.slice(0, 10).map((p, i) => (
                      <div key={i} className="hor-prog-row">
                        <span className="hor-prog-name">{p.program_name}</span>
                        <span className={`hor-prog-risk hor-prog-risk--${p.risk_tier || 'unknown'}`}>
                          {p.risk_tier?.toUpperCase() || '—'}
                        </span>
                        <span className="hor-prog-loc">{(p.lines_of_code || 0).toLocaleString()} LOC</span>
                      </div>
                    ))}
                    {allPrograms.length > 10 && (
                      <div className="hor-prog-more">+{allPrograms.length - 10} more programs</div>
                    )}
                  </div>
                ) : (
                  <div className="hor-unavailable">Program list not available.</div>
                )}
              </div>
            </div>
          )}

          {/* Service Split Candidates */}
          {clusters.length > 0 && (
            <div className="hor-panel">
              <div className="hor-panel-hdr">
                <span className="hor-panel-title">Service Split Candidates</span>
                <span className="hor-panel-badge">{clusters.length} group{clusters.length !== 1 ? 's' : ''}</span>
                <span className="hor-panel-subtitle">This program can be split into separate services</span>
              </div>
              <div className="hor-split-grid">
                {clusters.map((c, i) => <SplitCandidateCard key={c.id ?? i} cluster={c} index={i} />)}
              </div>
              {sharedFields.length > 0 && (
                <div className="hor-shared-row">
                  <span className="hor-shared-label">Shared across groups:</span>
                  {sharedFields.map((f, i) => <span key={i} className="hor-chip shared">{f}</span>)}
                </div>
              )}
            </div>
          )}

        </div>
      )}

      {/* ══════════════════ ACTIONS TAB ════════════════════════════════════════ */}
      {activeTab === 'actions' && (
        <div className="hor-tab-content">

          {allRecs.length === 0 ? (
            <div className="hor-panel">
              <div className="hor-panel-hdr"><span className="hor-panel-title">Recommendations</span></div>
              <div className="hor-unavailable">No recommendations found for this run.</div>
            </div>
          ) : (
            <>
              {/* Action summary strip */}
              <ActionSummaryStrip counts={counts} allRecs={allRecs} />

              {/* Issue breakdown chart */}
              <div className="hor-panel">
                <div className="hor-panel-hdr">
                  <span className="hor-panel-title">Issue Breakdown</span>
                  <span className="hor-panel-subtitle">What kinds of issues were found?</span>
                </div>
                <IssueBreakdownChart allRecs={allRecs} />
              </div>

              {/* Review list */}
              <div className="hor-panel">
                <div className="hor-panel-hdr">
                  <span className="hor-panel-title">Action Review</span>
                  <span className="hor-panel-badge">{totalRecs} total</span>
                </div>

                <div className="hor-filter-row">
                  {FILTERS.map(f => (
                    <button
                      key={f}
                      className={`hor-filter-btn ${f} ${filter === f ? 'active' : ''}`}
                      onClick={() => setFilter(f)}
                    >
                      {f === 'all' ? 'All' : f.charAt(0).toUpperCase() + f.slice(1)}
                      <span className="hor-filter-count">
                        {f === 'all' ? counts.all : f === 'pending' ? counts.pending : f === 'confirmed' ? counts.confirmed : counts.skipped}
                      </span>
                    </button>
                  ))}
                </div>

                <div className="hor-rec-list">
                  {filteredRecs.length === 0
                    ? <div className="hor-unavailable">No {filter} recommendations.</div>
                    : filteredRecs.map((rec, i) => <RecRow key={rec.id || i} rec={rec} />)
                  }
                </div>
              </div>
            </>
          )}

        </div>
      )}

    </div>
  )
}

// ── Priority card ─────────────────────────────────────────────────────────────

function PriorityCard({ rec }) {
  const color   = issueColor(rec.smell_type)
  const confPct = rec.confidence != null ? Math.round(rec.confidence * 100) : null

  return (
    <div className={`hor-priority-card ${color}`}>
      <div className="hor-priority-badge-row">
        <span className={`hor-issue-badge ${color}`}>{issueLabel(rec.smell_type)}</span>
      </div>
      <div className="hor-priority-title">{rec.title || issueLabel(rec.smell_type)}</div>
      {rec.why && <div className="hor-priority-why">{rec.why}</div>}
      {confPct != null && (
        <div className="hor-priority-conf-wrap">
          <span className="hor-priority-conf-label">{confPct}% confidence</span>
          <div className="hor-priority-conf-bar">
            <div className="hor-priority-conf-fill" style={{ width: `${confPct}%`, background: ISSUE_HEX[color] || '#7aa2f7' }} />
          </div>
        </div>
      )}
    </div>
  )
}
